<script lang="ts">
  import type { PlayerInterface } from '@foundation-player/loader';
  import { getContext } from 'svelte';
  import IconButton from '../../shared/components/IconButton.svelte';
  import Close from '../../shared/assets/icons/Close.svelte';
  import Play from '../../shared/assets/icons/Play.svelte';
  import { registerFocus } from '../../shared/focus-manager/focus.action.svelte.js';
  import type { IconButtonSize } from '../../shared/types/icon-button';
  import type { PlayerStateContext } from '../../shared/types/player-state';
  import type { UIStateContext } from '../types/internal/ui-state';
  import type { UIConfig } from '../types/ui-config';
  import { UIEvent } from '../types/ui-event';
  import Button from './Button.svelte';

  export let player: PlayerInterface;
  const size: IconButtonSize = 'm';

  const { sessionState, nextMediumMetadata } = getContext<PlayerStateContext>('playerState');
  const { nextMediumPreviewCurrentlyShown } = getContext<UIStateContext>('uiState');
  const { nextMediumPreviewTimeoutInSeconds = 5 } = getContext<UIConfig>('uiConfig');

  let timeLeftInSeconds = nextMediumPreviewTimeoutInSeconds;
  let nextMediumRequested = false;

  $: hasNextMediumMetadata = !!(
    $nextMediumMetadata?.headline ||
    $nextMediumMetadata?.poster ||
    $nextMediumMetadata?.duration
  );

  const emitNextMediumRequestEvent = () => {
    // make sure the event is only triggered once per medium
    if (nextMediumRequested === false) {
      nextMediumRequested = true;
      player.dispatchEvent(new Event(UIEvent.NextMediumRequest));
    }
  };

  const hideNextMediumPreview = () => nextMediumPreviewCurrentlyShown.set(false);

  const startCountdown = () => {
    const countdown = setInterval(() => {
      timeLeftInSeconds--;

      if (timeLeftInSeconds === 0) {
        emitNextMediumRequestEvent();
        clearInterval(countdown);
      }
    }, 1000);
  };

  $: if ($sessionState === 'ended') {
    timeLeftInSeconds = nextMediumPreviewTimeoutInSeconds;

    if ($nextMediumMetadata?.nextMediumAvailable) {
      nextMediumPreviewCurrentlyShown.set(true);
    }
    startCountdown();
  } else {
    hideNextMediumPreview();
  }

  $: if ($sessionState === 'active') {
    nextMediumRequested = false;
  }
</script>

{#if $nextMediumPreviewCurrentlyShown && $nextMediumMetadata}
  <div class="preview-next-medium-layer">
    <div class="close-button">
      <IconButton
        title="Automatisches Abspielen des nächsten Videos stoppen"
        {size}
        on:click={hideNextMediumPreview}
        focusLayer="nextMediumPreview"
        focusName="close"
      >
        <Close aria-hidden={true} />
      </IconButton>
    </div>

    <div class="content" class:has-next-medium-metadata={hasNextMediumMetadata}>
      <p role="timer" class="countdown-text">
        Nächstes Video in <span class="time-left">{timeLeftInSeconds}</span>
        {timeLeftInSeconds === 1 ? 'Sekunde' : 'Sekunden'}
      </p>

      {#if hasNextMediumMetadata}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class="preview-with-information"
          style={$nextMediumMetadata.poster
            ? `background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%), url(${$nextMediumMetadata.poster});`
            : 'background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%)'}
          on:click={emitNextMediumRequestEvent}
          data-testid="poster"
        >
          <button
            type="button"
            class="play-next-video-button"
            aria-label={$nextMediumMetadata.headline
              ? `Nächstes Video '${$nextMediumMetadata.headline}' ansehen`
              : 'Nächstes Video ansehen'}
            data-focusName="playNextMedium"
            data-focusLayer="nextMediumPreview"
            use:registerFocus={{ focusName: 'playNextMedium', focusLayer: 'nextMediumPreview' }}
          >
            <Play aria-hidden={true} />
          </button>

          {#if $nextMediumMetadata.headline || $nextMediumMetadata.duration}
            <dl class="next-medium-metadata" aria-label="Informationen über das nächste Video">
              {#if $nextMediumMetadata.duration}
                <dt class="visually-hidden">Dauer:</dt>
                <dd class="duration">{$nextMediumMetadata.duration}</dd>
              {/if}
              {#if $nextMediumMetadata.headline}
                <dt class="visually-hidden">Titel:</dt>
                <dd class="headline">{$nextMediumMetadata.headline}</dd>
              {/if}
            </dl>
          {/if}
        </div>
      {:else}
        <Button on:click={emitNextMediumRequestEvent} focusLayer="nextMediumPreview" focusName="playNextMedium">
          <Play aria-hidden="true" />Nächstes Video ansehen
        </Button>
      {/if}
    </div>
  </div>
{/if}

<style module>
  .preview-next-medium-layer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    background-color: var(--next-medium-preview-background-color);

    --icon-hover-color: var(--focus-background);
    --icon-hover-background-color: var(--focus-color);
  }

  .close-button {
    position: absolute;
    /* Min value (0px) ends at 400 px. Max value (20px) starts at 900 px. */
    /* https://modern-fluid-typography.vercel.app/ */
    top: clamp(0rem, 2vw - 0.5rem, 1rem);
    right: clamp(0rem, 2vw - 0.5rem, 1rem);
  }

  .countdown-text {
    /* Min value (14px) ends at 400 px. Max value (18px) starts at 667 px. */
    font-size: clamp(0.875rem, 1.5vw + 0.5rem, 1.125rem);
    margin: 0.5rem 0;
  }
  .time-left {
    font-weight: 700;
  }

  .content.has-next-medium-metadata {
    width: min(70cqw, 540px);
    display: flex;
    flex-direction: column;
  }
  .preview-with-information {
    position: relative;
    padding: 0;
    margin: 0;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: 16/9;
    background-color: rgb(85, 85, 85);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    overflow: hidden;
    border: none;
    cursor: pointer;
    /* Min value (14px) ends at 400 px. Max value (18px) starts at 667 px. */
    font-size: clamp(0.875rem, 1.5vw + 0.5rem, 1.125rem);
  }

  .preview-with-information:focus {
    outline: 2px solid #ffffff; /* Fallback for older browsers */
    outline: var(--outline);
  }

  @media (hover: hover) {
    .preview-with-information:hover {
      outline: var(--outline);
    }
  }

  @supports selector(:focus-visible) {
    /* Don't show the outline on focus... */
    .preview-with-information:focus,
    .play-next-video-button:focus {
      outline: none;
    }

    /* ...but show it on focus-visible */
    .preview-with-information:focus-visible,
    .play-next-video-button:focus-visible {
      outline: 2px solid #ffffff; /* Fallback for older browsers */
      outline: var(--outline);
    }
  }

  .play-next-video-button {
    margin: auto;
    padding: 0;
    color: var(--icon-color);
    background: rgba(0, 0, 0, 0.5);
    border: none;
    cursor: pointer;
    width: clamp(3rem, 17vw - 1.25rem, 7rem);
    height: clamp(3rem, 17vw - 1.25rem, 7rem);
  }

  .play-next-video-button :global(svg) {
    width: 60%;
    height: 60%;
  }

  .next-medium-metadata {
    display: flex;
    flex-direction: column;
    text-align: left;
    padding: clamp(0.5rem, 6vw - 1rem, 1rem);
    padding-top: 0; /* center button in space above vertically */
    margin: 0;
  }

  .next-medium-metadata > * {
    margin: 0; /* otherwise text has margin on the left */
  }

  @media (prefers-contrast: more) or (prefers-reduced-transparency: reduce) {
    .next-medium-metadata {
      background-color: var(--controls-no-transparency-background);
      padding: 0.5rem;
    }
  }

  .headline {
    font-weight: 700;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2; /* even though webkit, chrome and firefox support it */
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .duration {
    font-size: 0.85em;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    white-space: nowrap;
    opacity: 0;
  }
</style>
